import * as moment from 'moment'
import { Moment } from 'moment'
import { ClientConfig } from '../config/ClientConfig'
import { BadBeatJackpotToggleMode, SurgeToggleMode } from '../config/FeatureConfig'
import { checkCookie, getCookie } from './CookieStorageService'
import { ABExperimentService } from './ABExperimentService'
import { BlockMarketFeature } from './BlockMarketFeature'

/** Cookie overrides both enable and disable. cookie='true' → enabled, cookie='false' → disabled, no cookie → config. */
export function configOrCookieOverride(configKey: keyof typeof ClientConfig, cookieName: string): boolean {
	const cookie = getCookie(cookieName)
	if (cookie?.value === 'true') return true
	if (cookie?.value === 'false') return false
	return Boolean((ClientConfig as Record<string, unknown>)[configKey])
}

export const isWithinSurgeBetaPeriod = (currentDateTime: Moment) => {
	// 6 hours from 930AM perth time, utc offset of 8
	const betaStartDateTime = moment('2021-02-11 01:30+00:00')
	const betaEndDateTime = moment('2021-02-11 07:30+00:00')

	return currentDateTime.utc().isBetween(betaStartDateTime, betaEndDateTime)
}

class SurgeFeature {
	get isEnabled() {
		switch (ClientConfig.SURGE_FEATURE.mode) {
			case SurgeToggleMode.DISABLED:
				return false
			case SurgeToggleMode.ENABLED:
				return true
			case SurgeToggleMode.ENABLED_WITH_COOKIE:
				return checkCookie('surge-feature-toggle')
			case SurgeToggleMode.TIMED_BETA_ENABLED:
				return isWithinSurgeBetaPeriod(moment()) || checkCookie('surge-qc-test')
		}
	}
}

class DailyRewardPrizeDrawFeature {
	get IsTestAnimationEnabled() {
		return (
			ClientConfig.DAILY_REWARDS_PRIZE_DRAW_FEATURE.testAnimationEnabled ||
			checkCookie('daily-reward-prize-draw-test-animation')
		)
	}

	get isTwoStepFlowEnabled() {
		return ClientConfig.DAILY_REWARDS_PRIZE_DRAW_FEATURE.twoStepFlow || checkCookie('daily-reward-prize-draw-two-step')
	}
}

class GcOnlyMarketFeature {
	isEnabled(residentialState: string): boolean {
		if (!residentialState) return false

		const marketInfo = ClientConfig.GC_ONLY_MARKETS.find(market => market.knownEntries.includes(residentialState.toLowerCase()))
		if (!marketInfo) return false

		const blockDateCookie = getCookie(`GC_ONLY_${marketInfo.isoEntry}_START_TIME`)
		const blockStartDate = blockDateCookie ? Date.parse(blockDateCookie.value) : marketInfo.blockDate
		return (blockStartDate <= Date.now())
	}
}

class CasinoOnlyBlockedMarketFeature {
	isEnabled(residentialState: string, geoLocationState: string): boolean {
		if (!residentialState && !geoLocationState) return false

		const normalizedResidentialState = residentialState?.toLowerCase()
		const normalizedGeoLocationState = geoLocationState?.toLowerCase()
		const marketInfo = ClientConfig.CASINO_ONLY_BLOCKED_MARKETS.find(
			market =>
				(normalizedResidentialState && market.knownEntries.includes(normalizedResidentialState)) ||
				(normalizedGeoLocationState && market.knownEntries.includes(normalizedGeoLocationState)),
		)
		if (!marketInfo) return false

		const blockDateCookie = getCookie(`CASINO_ONLY_${marketInfo.isoEntry}_START_TIME`)
		const blockStartDate = blockDateCookie ? Date.parse(blockDateCookie.value) : marketInfo.blockDate
		return (blockStartDate <= Date.now())
	}
}

class AmoeCddFeature {
	isEnabled(): boolean {
		return ClientConfig.AMOE_CDD_ENABLED || checkCookie('amoe-hub')
	}
}

class SelfExclusionsPlayerAccountFeature {
	isEnabled(): boolean {
		return ClientConfig.SELF_EXCLUSIONS_PLAYER_ACCOUNT_ENABLED || checkCookie('selfexclusions-playeraccount')
	}
}

class SnGFeature {

	get allowPlayDuringDowntime() {
		return ClientConfig.POKER_SERVER_TOURNAMENTS.PLAY_DURING_DOWNTIME_ENABLED || checkCookie('PLAY_SNG_DURING_DOWNTIME_ENABLED')
	}
}

class MFAFeature {
	isEnabled() {
		return ClientConfig.MFA_ENABLED || checkCookie('MFA_ENABLED')
	}
}

class PrivacyChoiceFeature {
	isEnabled(): boolean {
		return ClientConfig.PRIVACY_CHOICE_ENABLED || checkCookie('PRIVACY_CHOICE_ENABLED')
	}
}

class BankVerificationFeature {
	// Bank Details verification is enabled and there is no canary OR
	// Bank Details verification is enabled, there is a canary and the user is part of the canary group
	isEnabled(userId: number): boolean {
		if (checkCookie('BANK_VERIFICATION')) return true
		if (ClientConfig.BANK_VERIFICATION.enabled &&
			ClientConfig.BANK_VERIFICATION.users.includes(userId)) return true
		return (
			ClientConfig.BANK_VERIFICATION.enabled &&
			(!ClientConfig.BANK_VERIFICATION.useCanary || ABExperimentService.bankDetailsVerification.isGroupA(userId))
		)
	}
}

class PlayerNotesMigration {
	isEnabled(): boolean {
		return ClientConfig.USE_MIGRATED_PLAYER_NOTES || checkCookie('USE_MIGRATED_PLAYER_NOTES')
	}
}

class RedeemableBalanceModalFeature {
	private userId: number = 0
	setUserId(userId: number) {this.userId = userId}

	isUserInABCanaryGroup(): boolean {
		if (this.userId === 0) return false
		return (this.userId + ClientConfig.REDEEMABLE_BALANCE.canarySeed) % 100 < ClientConfig.REDEEMABLE_BALANCE.canaryPercent
	}
	isUserInBetaGroup(): boolean {
		return ClientConfig.REDEEMABLE_BALANCE.betaGroupCubeiaIds.includes(globalMyPlayer.id)
	}
	isUserNotInExcludedGroup(): boolean {
		if (this.userId === 0) return true
		return !ClientConfig.REDEEMABLE_BALANCE.excludedGroupUserIds.includes(this.userId)
	}
	isEnabled(): boolean {
		const isUserIncludedInGroup = this.isUserInBetaGroup() || this.isUserInABCanaryGroup()

		return ClientConfig.REDEEMABLE_BALANCE.enabled && this.isUserNotInExcludedGroup() && isUserIncludedInGroup
	}
}

class DailyRewardSectionFeature {
	isEnabled(): boolean {
		return ClientConfig.ANIMATED_DAILY_BONUS_ENABLED || checkCookie('ANIMATED_DAILY_BONUS_ENABLED')
	}
}

class NewDailyRewardFeature {
	isEnabled(): boolean {
		return ClientConfig.NEW_DAILY_REWARD_FEATURE_ENABLED || checkCookie('NEW_DAILY_REWARD_FEATURE_ENABLED')
	}
}

class MobileBannerExperimentFeature {
	isEnabled(userId: number): boolean {
		if (checkCookie('MOBILE_BANNER_EXPERIMENT_FEATURE_ENABLED')) return true
		if (ClientConfig.MOBILE_BANNER_EXPERIMENT.enabled &&
			ClientConfig.MOBILE_BANNER_EXPERIMENT.users.includes(userId)) return true
		return (
			ClientConfig.MOBILE_BANNER_EXPERIMENT.enabled &&
			(!ClientConfig.MOBILE_BANNER_EXPERIMENT.useCanary ||
				ABExperimentService.mobileBannerExperimentSection.isGroupA(userId)))
	}
}

class BrazeFeature {
	private userId: number = 0
	setUserId(userId: number) {this.userId = userId}

	isCasinoBannerEnabled() {
		if (checkCookie('BRAZE_CASINO_BANNER_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.CASINO_BANNER_ENABLED
    }

	isPokerBannerEnabled() {
		if (checkCookie('BRAZE_POKER_BANNER_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.POKER_BANNER_ENABLED
	}

	isInAppMessagesEnabled() {
		if (checkCookie('BRAZE_LOGIN_NOTIFICATIONS_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.IN_APP_MESSAGES_ENABLED
	}

	isInboxEnabled() {
		if (checkCookie('BRAZE_INBOX_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.INBOX_ENABLED
	}

	isLogoBannerEnabled() {
		if (checkCookie('BRAZE_LOGO_BANNER_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.LOGO_BANNER_ENABLED
	}

	isTournamentFilterDeepLinkEnabled(): boolean {
		if (checkCookie('BRAZE_TOURNAMENT_LIST_FILTER_DEEPLINK_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.TOURNAMENT_FILTER_DEEP_LINK_ENABLED
	}

	isSnGDeepLinkEnabled(): boolean {
		if (checkCookie('BRAZE_SNG_LIST_DEEPLINK_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.SNG_DEEP_LINK_ENABLED
	}

	isJsnGDeepLinkEnabled(): boolean {
		return checkCookie('BRAZE_JSNG_LIST_DEEPLINK_FEATURE_ENABLED') || ClientConfig.BRAZE_FEATURES.JSNG_DEEP_LINK_ENABLED
	}

	isRingGameDeepLinkEnabled(): boolean {
		if (checkCookie('BRAZE_RING_GAME_LIST_DEEPLINK_FEATURE_ENABLED')) return true
		return ClientConfig.BRAZE_FEATURES.RING_GAME_DEEP_LINK_ENABLED
	}

	isContentCardsEnabled(): boolean {
		return this.isPokerBannerEnabled() || this.isCasinoBannerEnabled() || this.isLogoBannerEnabled()
	}

	isEnabled() {
		return this.isContentCardsEnabled() || this.isInAppMessagesEnabled()
	}
}

class GapBlackjackPremiumGameplayHistoryFeature {
	isEnabled(): boolean {
		if (checkCookie('GAP_BJK_PREMIUM_GAMEPLAY_HISTORY')) return true
		return ClientConfig.GAP_BJK_PREMIUM_GAMEPLAY_HISTORY_ENABLED
	}
}

class SlotsCloseButtonPositionFeature {
	isEnabled(gameId: string): boolean {
		if (checkCookie('SLOTS_CLOSE_BUTTON_POSITION')) return true
		return  ClientConfig.SLOTS_CLOSE_BUTTON_POSITION_ENABLED &&
			ClientConfig.SLOT_GAMES_CLOSE_BUTTON_POSITION_ENABLED.includes(gameId)
	}
}

class PlayerFeatureConfig {
	isEnabled(): boolean {
		return ClientConfig.PLAYER_FEATURE_CONFIG.enabled || checkCookie('PLAYER_FEATURE_CONFIG')
	}
	private isPlayerRestrictedInPhase(userId: string, phaseUserIds: string[]): boolean {
		return this.isEnabled() && phaseUserIds.includes(userId)
	}

	isPlayerRestrictedInPhase1(userId: string): boolean {
		return this.isPlayerRestrictedInPhase(userId, ClientConfig.PLAYER_FEATURE_CONFIG.phase1UserIds)
	}

	isPlayerRestrictedInPhase2(userId: string): boolean {
		return this.isPlayerRestrictedInPhase(userId, ClientConfig.PLAYER_FEATURE_CONFIG.phase2UserIds)
	}
}

class RingGamesFeature {
	get includeNewPokerServerRingGames() {
		return ClientConfig.POKER_SERVER_RING_GAMES_ENABLED || checkCookie('POKER_SERVER_RING_GAMES_ENABLED')
	}

	get allowPlayDuringDowntime() {
		return ClientConfig.POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED || checkCookie('POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED')
	}
}

class ScheduledTournamentsFeature {
	get includeNewPokerServerTournaments() {
		return ClientConfig.POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED || checkCookie('POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED')
	}
}

class FindGameFeature {
	isEnabled(): boolean {
		return ClientConfig.FIND_GAME.enabled
	}

	isLoggingEnabled(): boolean {
		return ClientConfig.FIND_GAME.loggingEnabled && this.isEnabled()
	}

	newUserTimeIntervalIs5MinTestingOnly(): boolean {
		return this.isEnabled() && checkCookie('NEW_USER_TIME_INTERVAL_IS_5_MIN')
	}
}

class RecentlyPlayedFeature {
	isEnabled(): boolean {
		return ClientConfig.RECENTLY_PLAYED_FEATURE_ENABLED || checkCookie('RECENTLY_PLAYED_FEATURE_ENABLED')
	}
}

class FirstTimeOfferFeature {
	isEnabled(): boolean {
		return ClientConfig.FIRST_TIME_OFFER_ENABLED || checkCookie('FIRST_TIME_OFFER_ENABLED')
	}
}

class PartialRedemptionsFeature {
	isEnabled(): boolean {
		return ClientConfig.PARTIAL_REDEMPTIONS_ENABLED || checkCookie('PARTIAL_REDEMPTIONS_ENABLED')
	}

	isMocking(): boolean {
		return this.isEnabled() && checkCookie('PARTIAL_REDEMPTIONS_MOCK')
	}
}

class ClientTriggerFeature {
	isFirstGameExitTriggerEnabled() {
		return ClientConfig.CLIENT_TRIGGERS.firstGameExitTriggerEnabled || checkCookie('FIRST_GAME_EXIT_TRIGGER_ENABLED')
	}

	isCashierModalOpenedTriggerEnabled() {
		return ClientConfig.CLIENT_TRIGGERS.cashierModalOpenedTriggerEnabled || checkCookie('CASHIER_MODAL_OPENED_TRIGGER_ENABLED')
	}
}

class PokerChallengeUIFeature {
	isEnabled(): boolean {
		return !(ClientConfig.POKER_CHALLENGE_UI_DISABLED || checkCookie('POKER_CHALLENGE_UI_DISABLED'))
	}
}

class GapCaribbeanPokerGameHistory {
	isEnabled(): boolean {
		return ClientConfig.GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED || checkCookie('GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED')
	}
}

class ReactGameTabsRefactor {
	isEnabled(userId: number): boolean {
		return this.isExperimentActiveForUser(userId) || checkCookie('REACT_GAME_TABS_ENABLED')
	}

	isExperimentActiveForUser(userId: number): boolean {
		return ClientConfig.REACT_GAME_TABS_ENABLED && ABExperimentService.reactGameTabsExperiment.isGroupA(userId)
	}
}

class GapGameTileAsset {
	isEnabled(): boolean {
		return ClientConfig.GAP_GAME_TILE_ASSET_ENABLED || checkCookie('GAP_GAME_TILE_ASSET_ENABLED')
	}
}

class PokerLobbySorting {
	isEnabled(): boolean {
		return ClientConfig.POKER_LOBBY_SORTING || checkCookie('POKER_LOBBY_SORTING')
	}
}



class HighHandChallengeFeature {
	isEnabled(): boolean {
		return ClientConfig.HIGH_HAND_CHALLENGE_ENABLED || checkCookie('HIGH_HAND_CHALLENGE_ENABLED')
	}
}
class SocialLiveCasinoLivestatFeature {
	isEnabled(userId: number): boolean {
		if (checkCookie('SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED')) return true
		return ClientConfig.SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED
	}

	shouldShowMinimumLimit(): boolean {
		if (checkCookie('SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT')) return true
		return ClientConfig.SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT
	}
}


class AllInOrFoldFeature {
	isEnabled(): boolean {
		return ClientConfig.ALL_IN_OR_FOLD_ENABLED || checkCookie('ALL_IN_OR_FOLD_ENABLED')
	}
}

class DailyBonusBrazeCustomEventFeature {
    isEnabled(): boolean {
        return ClientConfig.BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED || checkCookie('BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED')
    }
}

class ReferAFriendMenuFeature {
	isEnabled(): boolean {
		return ClientConfig.REFER_A_FRIEND_MENU_ENABLED || checkCookie('REFER_A_FRIEND_MENU_ENABLED')
	}
}

class ReferralProgramFeature {
	isEnabled(playerId: number): boolean {
		return ClientConfig.REFERRAL_PROGRAM_ENABLED || checkCookie('REFERRAL_PROGRAM_ENABLED')
	}
}

class TieredReferralsFeature {
	isEnabled(): boolean {
		return ClientConfig.TIERED_REFERRALS_ENABLED || checkCookie('TIERED_REFERRALS_ENABLED')
	}
}

class ReferralNudgeFeature {
	isEnabled(): boolean {
		return ClientConfig.REFERRAL_NUDGE_ENABLED || checkCookie('REFERRAL_NUDGE_ENABLED')
	}
}

class BbjFeature {
	isEnabled(userId: number): boolean {
		switch (ClientConfig.BBJ_FEATURE.mode) {
			case BadBeatJackpotToggleMode.ENABLED:
				return true
			case BadBeatJackpotToggleMode.DISABLED:
				return checkCookie('BBJ_FEATURE_ENABLED')
			case BadBeatJackpotToggleMode.STAFF_ONLY:
				return this.isStaff(userId)
		}
	}

	isStaff(userId: number) {
		return !!ClientConfig.BBJ_FEATURE.staffUserIds.find((u) => {
			return u === userId
		})
	}
}
  
class KycFirstPurchaseFeature {
    isNewSddSubmissionEndpointEnabled(): boolean {
        return ClientConfig.KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED || checkCookie('KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED')
    }

    isNewSddFlowEnabled(): boolean {
        return ClientConfig.KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED || checkCookie('KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED')
    }
}

class ToolbarV2Feature {
	isEnabled(): boolean {
		return ClientConfig.TOOLBAR_V2_ENABLED || checkCookie('TOOLBAR_V2_ENABLED')
	}
}

class LobbySkeletonFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('LOBBY_SKELETON_ENABLED', 'LOBBY_SKELETON_ENABLED')
	}
}

class GeoComplyFeature {
	private userId: number = 0

	setUserId(userId: number): void {
		this.userId = userId
	}

	private getCanaryPercentCookie(): number | null {
		const cookie = getCookie('GEO_COMPLY_CANARY_PERCENT')
		if (!cookie) return null
		const num = Number(cookie.value)
		if (!Number.isFinite(num) || num < 0 || num > 100) return null
		return num
	}

	isCanaryActive(): boolean {
		const config = (ClientConfig as Record<string, unknown>).GEO_COMPLY as { canaryPercent: number } | undefined
		if (config && config.canaryPercent > 0) return true
		if (this.getCanaryPercentCookie() != null) return true
		return false
	}

	private evaluateCanary(config: { canarySeed: number; betaGroupUserIds: number[]; excludedGroupUserIds: number[] }, canaryPercent: number): boolean {
		if (config.excludedGroupUserIds.includes(this.userId)) return false
		if (config.betaGroupUserIds.includes(this.userId)) return true
		if (this.userId === 0) return false
		return (this.userId + config.canarySeed) % 1000 < canaryPercent * 10
	}

	isEnabled(): boolean {
		const config = (ClientConfig as Record<string, unknown>).GEO_COMPLY as {
			canaryPercent: number
			canarySeed: number
			betaGroupUserIds: number[]
			excludedGroupUserIds: number[]
		} | undefined

		const cookiePercent = this.getCanaryPercentCookie()

		if (this.userId > 0 && config && (config.canaryPercent > 0 || cookiePercent != null)) {
			const canaryPercent = cookiePercent ?? config.canaryPercent
			return this.evaluateCanary(config, canaryPercent)
		}

		return false
	}
}

class GeoDebugPanelFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('GEO_DEBUG_PANEL_ENABLED', 'GEO_DEBUG_PANEL_ENABLED')
	}
}

class GeoSimulationFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('GEO_SIMULATION_ENABLED', 'GEO_SIMULATION_ENABLED')
	}
}

class GeoProtectionFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('GEO_PROTECTION_ENABLED', 'GEO_PROTECTION_ENABLED')
	}
}

class CddResidentialAddressFeature {
	isEnabled(): boolean {
		return ClientConfig.CDD_RESIDENTIAL_ADDRESS_ENABLED || checkCookie('CDD_RESIDENTIAL_ADDRESS_ENABLED')
	}
}

class CasinoExitServerCheckFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('CASINO_EXIT_SERVER_CHECK_ENABLED', 'CASINO_EXIT_SERVER_CHECK_ENABLED')
	}
}

class PazePaymentFeature {
	isEnabled(): boolean {
		return ClientConfig.PAZE_PAYMENT_ENABLED || checkCookie('PAZE_PAYMENT_ENABLED')
	}
}

class JsngLeaderboardFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('JSNG_LEADERBOARDS_ENABLED', 'JSNG_LEADERBOARDS_ENABLED')
	}
}

class JsngScMicroTierFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('JSNG_SC_MICRO_TIER_ENABLED', 'JSNG_SC_MICRO_TIER_ENABLED')
	}
}

class JsngPrizeHistoryMigrationFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED', 'JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED')
	}
}

class JsngRecentJackpotsFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('JSNG_RECENT_JACKPOTS_ENABLED', 'JSNG_RECENT_JACKPOTS_ENABLED')
	}
}

class JsngSc5MultiplierAndPrizeStructureFeature {
	isEnabled(): boolean {
		return configOrCookieOverride('JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED', 'JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED')
	}
}

export const FeatureToggleService = new (class {
	surge = new SurgeFeature()
	dailyRewardPrizeDrawFeature = new DailyRewardPrizeDrawFeature()
	blockMarketFeature = new BlockMarketFeature()
	gcOnlyMarketFeature = new GcOnlyMarketFeature()
	casinoOnlyBlockedMarketFeature = new CasinoOnlyBlockedMarketFeature()
	ameoCddFeature = new AmoeCddFeature()
	selfExclusionsPlayerAccountFeature = new SelfExclusionsPlayerAccountFeature()
	SnGsFeature = new SnGFeature()
	MFAFeature = new MFAFeature()
	privacyChoiceFeature = new PrivacyChoiceFeature()
	bankVerificationFeature = new BankVerificationFeature()
	playerNotesMigration = new PlayerNotesMigration()
	redeemableBalanceModalFeature = new RedeemableBalanceModalFeature()
	dailyRewardSectionFeature = new DailyRewardSectionFeature()
	newDailyRewardFeature = new NewDailyRewardFeature()
	MobileBannerExperimentFeature = new MobileBannerExperimentFeature()
	brazeFeature = new BrazeFeature()
	gapBlackjackPremiumGameplayHistoryFeature = new GapBlackjackPremiumGameplayHistoryFeature()
	slotsCloseButtonPositionFeature = new SlotsCloseButtonPositionFeature()
	playerFeatureConfig = new PlayerFeatureConfig()
	RingGamesFeature = new RingGamesFeature()
	ScheduledTournamentsFeature = new ScheduledTournamentsFeature()
	findGameFeature = new FindGameFeature()
	recentlyPlayedFeature = new RecentlyPlayedFeature()
	firstTimeOfferFeature = new FirstTimeOfferFeature()
	partialRedemptionsFeature = new PartialRedemptionsFeature()
	clientTriggerFeature = new ClientTriggerFeature()
	pokerChallengeUIFeature = new PokerChallengeUIFeature()
	gapCaribbeanPokerGameHistory = new GapCaribbeanPokerGameHistory()
	reactGameTabsRefactor = new ReactGameTabsRefactor()
	gapGameTileAsset = new GapGameTileAsset()
	pokerLobbySorting = new PokerLobbySorting()
	highHandChallengeFeature = new HighHandChallengeFeature()
	socialLiveCasinoLivestatFeature = new  SocialLiveCasinoLivestatFeature()
	allInOrFoldFeature = new AllInOrFoldFeature()
	dailyBonusBrazeCustomEventFeature = new DailyBonusBrazeCustomEventFeature()
	referAFriendMenuFeature = new ReferAFriendMenuFeature()
	referralProgramFeature = new ReferralProgramFeature()
	tieredReferralsFeature = new TieredReferralsFeature()
	referralNudgeFeature = new ReferralNudgeFeature()
	bbjFeature = new BbjFeature()
	kycFirstPurchaseFeature = new KycFirstPurchaseFeature()
	toolbarV2Feature = new ToolbarV2Feature()
	lobbySkeletonFeature = new LobbySkeletonFeature()
	geoComplyFeature = new GeoComplyFeature()
	geoDebugPanelFeature = new GeoDebugPanelFeature()
	geoSimulationFeature = new GeoSimulationFeature()
	geoProtectionFeature = new GeoProtectionFeature()
	cddResidentialAddressFeature = new CddResidentialAddressFeature()
	casinoExitServerCheckFeature = new CasinoExitServerCheckFeature()
	pazePaymentFeature = new PazePaymentFeature()
	jsngLeaderboardFeature = new JsngLeaderboardFeature()
	jsngScMicroTierFeature = new JsngScMicroTierFeature()
	jsngPrizeHistoryMigrationFeature = new JsngPrizeHistoryMigrationFeature()
	jsngRecentJackpotsFeature = new JsngRecentJackpotsFeature()
	jsngSc5MultiplierAndPrizeStructureFeature = new JsngSc5MultiplierAndPrizeStructureFeature()
})()
